import ChipListComponent from '../ChipListComponent';
import * as React from 'react';
import { FormEvent, useEffect, useState } from 'react';
import TextFieldComponent from '../TextFieldComponent';
import { Grid2, Box } from '@mui/material';
import ButtonComponent from '../ButtonComponent';
import { Check, Mail } from '@mui/icons-material';
import zod from 'zod';
import DropSelectComponent from '../DropSelectComponent';
import { getSmtpIntegrationsAsync } from '../../services/Devices';

interface IDropSelectOptions {
  value: any;
  option: string;
  description?: string;
}

interface MailSelectComponentProps {
  onChange?: (value: string[]) => void;
  value?: string[];
  gridSize?: number;
  MailChipGridSize?: number;
  maxItemLength?: number;
  smtpValue?: any;
  onSmtpChange?: (value: any) => void;
  smtpGridSize?: number;
  smtpLabel?: string;
}

const MailSelectComponent = ({
  onChange,
  MailChipGridSize = 12,
  gridSize = 8,
  value,
  maxItemLength = 254,
  smtpValue,
  onSmtpChange,
  smtpGridSize = 4,
  smtpLabel = 'SMTP',
}: MailSelectComponentProps) => {
  const [selectedMail, setselectedMail] = useState<string[]>([]);
  const [mail, setMail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [smtpOptions, setSmtpOptions] = useState<IDropSelectOptions[]>([]);

  const emailSchema = zod
    .string()
    .email('Formato de e-mail inválido')
    .max(maxItemLength, `O e-mail não pode ter mais de ${maxItemLength} caracteres`);

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setError('O e-mail não pode estar vazio');
      return false;
    }

    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!validateEmail(mail)) {
      return;
    }

    setselectedMail((oldState) => [...oldState, mail]);
    setMail('');
    if (onChange) onChange([...selectedMail, mail]);
  };

  const handleMailChange = (value: string) => {
    if (value.length <= maxItemLength) {
      setMail(value);
      if (error) setError('');
    }
  };

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setselectedMail(value);
    } else {
      setselectedMail([]);
    }
  }, [value]);

  useEffect(() => {
    getSmtpIntegrationsAsync(true, smtpValue)
      .then(smtps => {
        setSmtpOptions(
          smtps.map(s => ({
            value: s.id,
            option: `${s.host}:${s.port}${s.isActive ? '' : ' [Inativo]'}`,
            disabled: !s.isActive,
          }))
        );
      });
  }, [smtpValue]);

  return (
    <>
      {smtpOptions.length > 0 ? (
        <Grid2 size={{ xs: 12, md: smtpGridSize }} sx={{ mb: { xs: 2, md: 0 } }}>
          <DropSelectComponent
            label={smtpLabel}
            options={smtpOptions}
            valueSelected={smtpValue}
            onChange={(value) => {
              onSmtpChange?.(value === '' ? null : value);
            }}
            isRequired={false}
          />
        </Grid2>
      ) : (
        <Grid2 size={{ xs: 12, md: smtpGridSize }} sx={{ mb: { xs: 2, md: 0 } }}>
          <Box
            sx={{
              height: '56px',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              paddingX: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.09)',
              opacity: 0.6,
              cursor: 'not-allowed',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1rem'
            }}
          >
            Nenhum SMTP
          </Box>
        </Grid2>
      )}
      <Grid2 size={{ xs: 12, md: gridSize }}>
        <form style={{ display: 'flex' }} onSubmit={handleSubmit}>
          <TextFieldComponent
            value={mail}
            onChangeText={handleMailChange}
            type='email'
            placeholder='usuario@email.com'
            error={!!error}
            helperText={error}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderBottomRightRadius: 0,
                  borderTopRightRadius: 0,
                },
              },
            }}
            label={'Endereços de E-mail para notificações'}
          />
          <ButtonComponent
            type="submit"
            disabled={!mail.trim()}
            sx={{
              mb: 3,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
              height: '70%',
            }}
          >
            <Check />
          </ButtonComponent>
        </form>
      </Grid2>
      {selectedMail && selectedMail.length > 0 && (
        <Grid2 size={MailChipGridSize} sx={{ mb: 1 }}>
          <ChipListComponent
            chipIcon={<Mail />}
            chipData={selectedMail}
            maxChipWidth={250}
            onDelete={(index) => {
              const newVal = selectedMail.filter((_, i) => i !== index);
              if (onChange) onChange(newVal);
              setselectedMail(newVal);
            }}
          />
        </Grid2>
      )}
    </>
  );
};

export default MailSelectComponent;
